import { useUploadCrunch } from '@/app/(dashboard)/upload/UploadCrunchProvider'
import {
  Button,
  FormControl,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFSTrackEvent,
} from '@/dc-shared-web/src'
import { zodResolver } from '@hookform/resolvers/zod'
import { FC } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'
import { FileItem } from '@/app/(dashboard)/components/FileItem'
import { PickFile } from '@/app/(dashboard)/components/PickFile'
import { LocationSelect } from '@/app/(dashboard)/upload/step1/LocationSelect'
import { step1Schema } from '@/app/(dashboard)/upload/step1/Step1Schema'
import { LocationType } from '@/app/(dashboard)/projects/FolderSelectionTree'
import { FS, FS_CATEGORY } from '@/dc-shared-web/src/lib/trackingConstants'

export type Step1Props = {
  crunchInitialLocation: LocationType
  disableLocationSelect?: boolean
  onCancel: () => void
  teamUuid: string
}

export const Step1: FC<Step1Props> = ({
  onCancel,
  disableLocationSelect = false,
  crunchInitialLocation,
  teamUuid,
}) => {
  const { nextStep, updateStepsData, stepsData } = useUploadCrunch()
  const { trackEvent } = useFSTrackEvent()

  const form = useForm<z.infer<typeof step1Schema>>({
    resolver: zodResolver(step1Schema),
    defaultValues: {
      file: stepsData.step1.file,
      location: stepsData.step1.location || crunchInitialLocation,
    },
  })

  const onFileSelected = (file: File) => {
    form.setValue('file', file)
    form.trigger('file')

    trackEvent(FS.UPLOAD_FILE_ADDED, {
      page_category: FS_CATEGORY.UPLOAD,
    })
  }

  const onSubmit = () => {
    if (!form.formState.isValid) return

    const data = form.getValues()

    updateStepsData('step1', {
      file: data.file,
      location: data.location,
    })

    nextStep()
  }

  const file = useWatch({
    control: form.control,
    name: 'file',
  })

  const onFileRemove = () => {
    form.setValue('file', undefined)
    form.trigger('file')
  }

  const fileError = form.formState.errors.file?.message
  const isFormValid = form.formState.isValid

  return (
    <div className='tw-space-y-4'>
      <p>Upload a file and choose a destination. You can move it later.</p>
      <div className='tw-space-y-4'>
        {file ? (
          <FileItem
            file={file}
            onRemove={onFileRemove}
            errorMessage={fileError}
          />
        ) : (
          <PickFile onFileSelected={onFileSelected} />
        )}
        <Form {...form}>
          <FormField
            control={form.control}
            name='location'
            render={({ field }) => (
              <FormItem>
                <FormLabel required={!disableLocationSelect}>
                  Destination (project or folder)
                </FormLabel>
                <FormControl>
                  <LocationSelect
                    value={field.value}
                    onChange={(location) => {
                      form.setValue('location', location)
                      form.trigger('location')
                    }}
                    disabled={disableLocationSelect}
                    teamUuid={teamUuid}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </Form>
      </div>
      <div className='tw-text-right tw-space-x-2'>
        <Button
          onClick={() => {
            trackEvent(FS.BUTTON_CLICKED, {
              name: 'Cancel',
              page_category: FS_CATEGORY.UPLOAD,
            })
            onCancel()
          }}
          variant='outline'
          data-testid='cancel-button'
        >
          Cancel
        </Button>
        <Button
          disabled={!isFormValid}
          onClick={() => {
            trackEvent(FS.BUTTON_CLICKED, {
              name: 'Next',
              page_category: FS_CATEGORY.UPLOAD,
            })
            form.handleSubmit(onSubmit)()
          }}
          type='button'
          data-testid='next-button'
        >
          Next
        </Button>
      </div>
    </div>
  )
}
